import { resolveApiBase, SportsApiClient, type WidgetProps } from '@sports/core'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'
import { leagueTableStyles } from '../league-table'

/**
 * A cricket series points table. Cricket has no live `<id>_table` feed under
 * its scores tree: the tables are imported per series into `cricket-standings`
 * and served by `/v1/cricket/standings/:seriesId` (`{id, name, rows[]}`), with
 * `/v1/cricket/standings` listing every series. With `series` set the widget
 * shows that table; without it, a dropdown of every stored series (first one
 * selected) so an editor can find the id.
 */
export const CRICKET_TABLE_ATTRIBUTES = [
  'series',
  'lang',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface Props extends WidgetProps {
  series?: string
  lang?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

/** A `cricket-standings` row as the importer writes it (strings, '' when blank). */
interface StoredRow {
  position?: string
  teamId?: string
  team?: string
  played?: string
  won?: string
  drawn?: string
  lost?: string
  points?: string
}
interface StoredTable {
  id: string
  name: string
  rows: StoredRow[]
}
interface StoredTableList {
  sport: string
  tables: StoredTable[]
}

export const cricketTableStyles = `
  ${leagueTableStyles}
  .ck-bar { padding: 0.6rem 1rem; border-bottom: 1px solid var(--sports-border); }
  .ck-select {
    width: 100%; border: 1px solid var(--sports-border); background: var(--sports-bg);
    color: var(--sports-fg); border-radius: 8px; padding: 0.35rem 0.7rem; font: inherit; font-weight: 500;
  }
`

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}
const cellText = (v: string | undefined): string => (v == null || v === '' ? '—' : v)

export function CricketTable({ series, lang = 'es', limit, heading, refresh, apiBase }: Props) {
  const max = number(limit, 30)
  const refreshSeconds = number(refresh, 600)
  const base = resolveApiBase(apiBase)
  const client = new SportsApiClient(base)
  const t = useLabels(base, lang)
  const [expanded, setExpanded] = useState(false)
  const [picked, setPicked] = useState<string | null>(null)
  const seriesId = series?.trim() || ''

  // One shape either way: a pinned series is a one-table list.
  const { data, error, loading } = useFeed<StoredTable[]>(
    async (signal) =>
      seriesId
        ? [await client.standings<StoredTable>('cricket', seriesId, signal)]
        : (await client.standingsList<StoredTableList>('cricket', signal)).tables ?? [],
    [base, seriesId],
    refreshSeconds,
  )

  const list = data ?? []
  const table: StoredTable | null = list.find((x) => x.id === picked) ?? list[0] ?? null
  const rows = (table?.rows ?? []).slice(0, max)
  const visible = expanded ? rows : rows.slice(0, 10)
  const hasDraw = rows.some((r) => r.drawn && r.drawn !== '0')
  const hasPoints = rows.some((r) => r.points && r.points !== '')
  const title = heading || table?.name || 'Cricket'
  const crest = (r: StoredRow): string | undefined =>
    r.teamId ? `${base}/v1/logos/cricket/teams/${r.teamId}${r.team ? `?name=${encodeURIComponent(r.team)}` : ''}` : undefined

  return (
    <div class="sports-card">
      <div class="sports-head">
        <span>{title}</span>
      </div>

      {!seriesId && list.length > 1 && (
        <div class="ck-bar">
          <select class="ck-select" value={table?.id ?? ''} onChange={(e) => setPicked((e.target as HTMLSelectElement).value || null)}>
            {list.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name} · {x.id}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && <div class="sports-note">{t('loading', 'Cargando…')}</div>}
      {!loading && error && (
        <div class="sports-note error">{t('tableUnavailable', 'La clasificación no está disponible.')}</div>
      )}
      {!loading && !error && rows.length === 0 && (
        <div class="sports-note">{t('noSeriesStandings', 'Sin clasificación para esta serie.')}</div>
      )}

      {rows.length > 0 && (
        <div class="table-wrap">
          <table class="sports-table">
            <thead>
              <tr>
                <th>#</th>
                <th class="teamh">{t('team', 'Equipo')}</th>
                <th>{t('played', 'PJ')}</th>
                <th>{t('won', 'G')}</th>
                <th>{t('lost', 'P')}</th>
                {hasDraw && <th>{t('drawnNr', 'E/NR')}</th>}
                {hasPoints && <th>{t('points', 'Pts')}</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map((r, index) => {
                const logo = crest(r)
                return (
                  <tr key={`${r.teamId ?? ''}|${r.team ?? index}`}>
                    <td class="rank">{r.position || index + 1}</td>
                    <td class="team">
                      <span class="teamcell">
                        {logo && <img src={logo} alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />}
                        <span class="nm">{r.team ?? ''}</span>
                      </span>
                    </td>
                    <td>{cellText(r.played)}</td>
                    <td>{cellText(r.won)}</td>
                    <td>{cellText(r.lost)}</td>
                    {hasDraw && <td>{cellText(r.drawn)}</td>}
                    {hasPoints && <td class="pts">{cellText(r.points)}</td>}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 10 && (
        <button class="see-more" type="button" onClick={() => setExpanded(!expanded)}>
          {expanded ? t('seeLess', 'Ver menos') : `${t('seeMore', 'Ver más')} (${rows.length})`}
        </button>
      )}
    </div>
  )
}
